import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, MagnifyingGlass, X, EnvelopeSimple, PencilSimple, Trash, Upload, DownloadSimple, Calendar, ConfettiIcon } from "@phosphor-icons/react";

export default function StakeholderDirectory() {
  const { user } = useAuth();
  const tier = user?.ux_tier ?? 0;
  const canEdit = tier >= 3;
  const canDelete = tier >= 4;
  const canImport = tier >= 5;
  const canHoliday = tier >= 4;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;
  const [industries, setIndustries] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [holidayOpen, setHolidayOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        q: q || undefined,
        industry: industry || undefined,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      };
      if (selectedTags.length) params.tags = selectedTags.join(",");
      const [list, ind, tags] = await Promise.all([
        api.get("/stakeholders", { params }),
        api.get("/stakeholders/industries"),
        api.get("/stakeholders/tags"),
      ]);
      setItems(list.data.items || []);
      setTotal(list.data.total || 0);
      setIndustries(ind.data.industries || []);
      setAllTags(tags.data.tags || []);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [industry, selectedTags, page]);
  useEffect(() => {
    const t = setTimeout(() => {
      // setPage(0) triggers the page effect → which calls load(). No second call needed.
      if (page !== 0) setPage(0); else load();
    }, 350);
    return () => clearTimeout(t);
  /* eslint-disable-next-line */
  }, [q]);

  const toggleAll = () => selected.size === items.length ? setSelected(new Set()) : setSelected(new Set(items.map(i => i.stakeholder_id)));
  const toggle = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };
  const toggleTagFilter = (t) => setSelectedTags(s => s.includes(t) ? s.filter(x => x !== t) : [...s, t]);

  const removeStakeholder = async (s) => {
    if (!canDelete) { alert("Direct delete requires ux_tier ≥ 4. Use the deletion-workflow Request Delete button instead."); return; }
    if (!window.confirm(`Soft-delete ${s.full_name || s.company}? Will be recoverable from /recycle-bin.`)) return;
    try { await api.delete(`/stakeholders/${s.stakeholder_id}`); await load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const [importBusy, setImportBusy] = useState(false);
  const runImport = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) {
      alert("Please pick an .xlsx file.");
      e.target.value = "";
      return;
    }
    if (!window.confirm(`Import "${file.name}"?\n\nThis is idempotent — duplicates are merged by (company + email).`)) {
      e.target.value = "";
      return;
    }
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/stakeholders/import-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const errLine = (data.errors && data.errors.length) ? `\n\n⚠ ${data.errors.length} row error(s):\n${data.errors.slice(0, 5).join("\n")}` : "";
      alert(`Import done from "${data.source}" (sheet: ${data.sheet})\n\nInserted: ${data.inserted}\nUpdated: ${data.updated}\nSkipped: ${data.skipped}${errLine}`);
      await load();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setImportBusy(false);
      e.target.value = "";
    }
  };

  const exportCsv = async () => {
    try {
      const ids = selected.size ? Array.from(selected) : [];
      const { data } = await api.post("/stakeholders/export-csv", { stakeholder_ids: ids });
      const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename || "stakeholders.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || "Export failed"); }
  };

  const exportAll = async (fmt) => {
    try {
      const { data } = await api.get(`/reports/stakeholders.${fmt}`, { responseType: "blob" });
      const blob = new Blob([data]);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `span_stakeholders.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || "Export failed"); }
  };

  const selectedItems = useMemo(() => items.filter(i => selected.has(i.stakeholder_id)), [items, selected]);

  return (
    <div className="space-y-5" data-testid="stakeholders-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">Administration</div>
          <h1 className="font-display font-bold text-[28px]">Stakeholder Directory</h1>
          <p className="text-[12px] text-[var(--span-text-2)] mt-1 max-w-[640px]">
            All external partners — clients, law firms, industry associations, regulators —
            in one searchable, filterable list. Select rows to send a personalised
            email or WhatsApp blast (newsletters, organisational updates, greetings).
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canHoliday && (
            <button onClick={() => setHolidayOpen(true)} data-testid="holiday-scheduler" className="span-btn span-btn-ghost h-9 text-[12px]" title="Auto-queue greetings for Diwali / New Year / Holi at 9am IST">
              <ConfettiIcon size={13} /> Holiday auto-send
            </button>
          )}
          <button onClick={exportCsv} data-testid="export-csv" className="span-btn span-btn-ghost h-9 text-[12px]">
            <DownloadSimple size={13} /> Export CSV{selected.size ? ` (${selected.size})` : ""}
          </button>
          <div className="flex items-center gap-0.5 bg-[var(--surface,#fff)] border border-[var(--border)] rounded h-9" data-testid="export-all-formats">
            <span className="text-[10px] text-[var(--text-muted)] px-2">All as:</span>
            {["xlsx","docx","pdf"].map(f => (
              <button
                key={f}
                onClick={() => exportAll(f)}
                data-testid={`export-all-${f}`}
                className="text-[10px] uppercase font-mono px-2 hover:bg-[var(--surface-2)] h-full">{f}</button>
            ))}
          </div>
          {canImport && (
            <label data-testid="import-xlsx" className={`span-btn span-btn-ghost h-9 text-[12px] cursor-pointer ${importBusy ? "opacity-50 pointer-events-none" : ""}`}>
              <Upload size={13} /> {importBusy ? "Importing…" : "Import from Excel"}
              <input
                type="file"
                accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={runImport}
                data-testid="import-xlsx-input"
                className="hidden"
              />
            </label>
          )}
          {canEdit && (
            <button onClick={() => setEditing({})} data-testid="new-stakeholder" className="span-btn h-9 text-[12px]">
              <Plus size={13} /> Add stakeholder
            </button>
          )}
        </div>
      </div>

      {allTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="tag-chips">
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--span-text-2)] mr-1">Tags:</span>
          {allTags.slice(0, 12).map(t => {
            const active = selectedTags.includes(t.tag);
            return (
              <button
                key={t.tag}
                onClick={() => toggleTagFilter(t.tag)}
                data-testid={`tag-chip-${t.tag}`}
                className={`text-[10px] px-2 py-1 border rounded-sm transition-colors ${active ? "bg-[var(--span-accent,#F55A1E)] text-white border-[var(--span-accent,#F55A1E)]" : "border-[var(--span-border)] hover:bg-[var(--span-surface-3)]"}`}
              >
                #{t.tag} <span className="opacity-60 font-mono">({t.count})</span>
              </button>
            );
          })}
          {selectedTags.length > 0 && (
            <button onClick={() => setSelectedTags([])} className="text-[10px] underline text-[var(--span-text-2)] ml-1">Clear tags</button>
          )}
        </div>
      )}

      <div className="card-flat p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--span-text-2)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, company, email, designation…"
            data-testid="stk-search"
            className="span-input pl-8 h-9 w-full text-[12px]"
          />
        </div>
        <select value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="stk-industry-filter" className="span-input h-9 text-[12px] min-w-[180px]">
          <option value="">All industries ({items.length})</option>
          {industries.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
        {selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] font-mono uppercase tracking-[0.12em] text-[var(--span-text-2)]">{selected.size} selected</span>
            <button onClick={() => setComposeOpen(true)} data-testid="stk-bulk-send" className="span-btn h-9 text-[12px]">
              <EnvelopeSimple size={13} /> Send communication
            </button>
            <button onClick={() => setSelected(new Set())} className="span-btn span-btn-ghost h-9 text-[12px]"><X size={12} /></button>
          </div>
        )}
      </div>

      <div className="card-flat overflow-hidden">
        <table className="span-table w-full" data-testid="stk-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" data-testid="stk-select-all" checked={selected.size === items.length && items.length > 0} onChange={toggleAll} />
              </th>
              <th>Company</th>
              <th>Industry</th>
              <th>Contact</th>
              <th>Designation</th>
              <th>Email</th>
              <th>Phone</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-8 text-[var(--span-text-2)] font-mono text-[11px]">LOADING…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-[12px] text-[var(--span-text-2)]">
                No stakeholders match. {canImport && <>Click <strong>Import from Excel</strong> to load the master list.</>}
              </td></tr>
            )}
            {items.map(s => (
              <tr key={s.stakeholder_id} data-testid={`stk-row-${s.stakeholder_id}`} className={selected.has(s.stakeholder_id) ? "bg-[var(--span-surface-3)]" : ""}>
                <td><input type="checkbox" checked={selected.has(s.stakeholder_id)} onChange={() => toggle(s.stakeholder_id)} /></td>
                <td className="font-medium">{s.company}</td>
                <td className="text-[11px]"><span className="badge">{s.industry || "—"}</span></td>
                <td>{s.full_name || "—"}</td>
                <td className="text-[12px] text-[var(--span-text-2)] max-w-[220px] truncate" title={s.designation}>{s.designation || "—"}</td>
                <td className="text-[11px] font-mono"><a href={`mailto:${s.email}`} className="hover:underline">{s.email || "—"}</a></td>
                <td className="text-[11px] font-mono">{s.phone || "—"}</td>
                <td className="text-right">
                  <div className="flex items-center gap-1 justify-end">
                    {canEdit && (
                      <button onClick={() => setEditing(s)} data-testid={`stk-edit-${s.stakeholder_id}`} className="h-7 w-7 inline-flex items-center justify-center border border-[var(--span-border)] hover:bg-[var(--span-surface-3)]" title="Edit">
                        <PencilSimple size={12} />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => removeStakeholder(s)} data-testid={`stk-del-${s.stakeholder_id}`} className="h-7 w-7 inline-flex items-center justify-center border border-[var(--span-border)] hover:bg-[var(--span-surface-3)] text-[var(--span-danger)]" title="Delete">
                        <Trash size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-[var(--border)] text-[11px] bg-[var(--surface-2)]" data-testid="stk-pagination">
            <div className="text-[var(--text-muted)]">
              Showing <span className="font-mono font-bold">{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)}</span> of <span className="font-mono font-bold">{total}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="stk-prev"
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                className="px-2 py-1 hairline disabled:opacity-30 hover:bg-[var(--surface)]">‹ Prev</button>
              <span className="px-2 font-mono">{page + 1} / {Math.ceil(total / PAGE_SIZE)}</span>
              <button
                data-testid="stk-next"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage(p => p + 1)}
                className="px-2 py-1 hairline disabled:opacity-30 hover:bg-[var(--surface)]">Next ›</button>
            </div>
          </div>
        )}
      </div>

      {editing !== null && <EditModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {composeOpen && <ComposeModal recipients={selectedItems} onClose={() => setComposeOpen(false)} onSent={() => { setComposeOpen(false); setSelected(new Set()); }} />}
      {holidayOpen && <HolidayModal onClose={() => setHolidayOpen(false)} />}
    </div>
  );
}

function EditModal({ initial, onClose, onSaved }) {
  const isNew = !initial?.stakeholder_id;
  const [form, setForm] = useState({
    company: initial?.company || "", industry: initial?.industry || "",
    first_name: initial?.first_name || "", last_name: initial?.last_name || "",
    full_name: initial?.full_name || "", function: initial?.function || "",
    designation: initial?.designation || "", email: initial?.email || "",
    phone: initial?.phone || "", whatsapp: initial?.whatsapp || "",
    address: initial?.address || "", website: initial?.website || "",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const cleaned = { ...form };
      if (!cleaned.email) delete cleaned.email;  // EmailStr won't accept empty
      if (isNew) await api.post("/stakeholders", cleaned);
      else await api.put(`/stakeholders/${initial.stakeholder_id}`, cleaned);
      onSaved();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card-flat w-full max-w-[640px] max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3.5 border-b border-[var(--span-border)] flex items-center justify-between">
          <div>
            <div className="label-eyebrow">{isNew ? "New stakeholder" : "Edit stakeholder"}</div>
            <div className="font-display font-bold text-[16px]">{form.company || "(unnamed company)"}</div>
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={save} className="px-5 py-5 grid grid-cols-2 gap-3 text-[12px]">
          <Field label="Company *" v={form.company} req onChange={(v) => setForm({ ...form, company: v })} testid="f-company" />
          <Field label="Industry" v={form.industry} onChange={(v) => setForm({ ...form, industry: v })} testid="f-industry" />
          <Field label="First name" v={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="f-first" />
          <Field label="Last name" v={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="f-last" />
          <Field label="Full name" v={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="f-full" />
          <Field label="Designation" v={form.designation} onChange={(v) => setForm({ ...form, designation: v })} testid="f-desg" />
          <Field label="Function" v={form.function} onChange={(v) => setForm({ ...form, function: v })} testid="f-func" />
          <Field label="Email" v={form.email} type="email" onChange={(v) => setForm({ ...form, email: v })} testid="f-email" />
          <Field label="Phone" v={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="f-phone" />
          <Field label="WhatsApp" v={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} testid="f-wa" />
          <Field label="Website" v={form.website} onChange={(v) => setForm({ ...form, website: v })} testid="f-web" />
          <Field label="Address" v={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="f-addr" />
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1.5">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="span-input min-h-[60px]" data-testid="f-notes" />
          </div>
          {err && <div className="col-span-2 text-[var(--span-danger)] text-[12px]">{err}</div>}
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t border-[var(--span-border)]">
            <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
            <button data-testid="f-submit" disabled={busy} className="span-btn disabled:opacity-50">{busy ? "Saving…" : isNew ? "Add stakeholder" : "Save changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", req, testid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1.5">{label}</label>
      <input type={type} value={v || ""} required={req} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="span-input" />
    </div>
  );
}

function ComposeModal({ recipients, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [tplKey, setTplKey] = useState("");
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(() => {
    // default: next Monday 9am IST
    const d = new Date();
    const day = d.getDay();
    const daysUntilMon = (1 + 7 - day) % 7 || 7;
    d.setDate(d.getDate() + daysUntilMon);
    d.setHours(9, 0, 0, 0);
    return d.toISOString().slice(0, 16);  // for datetime-local input
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/stakeholders/templates").then(r => setTemplates(r.data.templates || [])).catch(() => {}); }, []);

  const applyTpl = (k) => {
    setTplKey(k);
    const t = templates.find(x => x.key === k);
    if (t) { setSubject(t.subject); setBody(t.body); }
  };

  const send = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setResult(null);
    try {
      const body_payload = {
        stakeholder_ids: recipients.map(r => r.stakeholder_id),
        channel, subject, body,
        template_key: tplKey || undefined,
        test_mode: testMode,
      };
      if (scheduleEnabled) body_payload.scheduled_at = new Date(scheduledAt).toISOString();
      const { data } = await api.post("/stakeholders/bulk-send", body_payload);
      setResult(data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card-flat w-full max-w-[720px] max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-3.5 border-b border-[var(--span-border)] flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Bulk communication</div>
            <div className="font-display font-bold text-[16px]">{recipients.length} recipient{recipients.length === 1 ? "" : "s"} selected</div>
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>

        {result ? (
          <div className="px-5 py-5 space-y-3">
            <div className="text-[14px] font-semibold text-[var(--span-success,#10B981)]">Queued successfully ✓</div>
            <table className="span-table text-[12px]">
              <tbody>
                <tr><td>Recipients</td><td className="font-mono">{result.recipients_total}</td></tr>
                <tr><td>Emails queued</td><td className="font-mono">{result.queued_email}</td></tr>
                <tr><td>WhatsApp queued</td><td className="font-mono">{result.queued_whatsapp}</td></tr>
                <tr><td>Skipped</td><td className="font-mono">{result.skipped_count}</td></tr>
              </tbody>
            </table>
            <div className="text-[11px] text-[var(--span-text-2)] leading-relaxed">{result.note_email_delivery}</div>
            <div className="text-[11px] text-[var(--span-text-2)] leading-relaxed">{result.note_whatsapp_delivery}</div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onSent} className="span-btn">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={send} className="px-5 py-5 space-y-4">
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="label-eyebrow block mb-1.5">Template (optional)</label>
                <select value={tplKey} onChange={(e) => applyTpl(e.target.value)} data-testid="bulk-template" className="span-input">
                  <option value="">— Custom —</option>
                  {templates.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Channel</label>
                <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="bulk-channel" className="span-input">
                  <option value="email">Email only</option>
                  <option value="whatsapp">WhatsApp only</option>
                  <option value="both">Both</option>
                </select>
              </div>
            </div>

            {(channel === "email" || channel === "both") && (
              <div>
                <label className="label-eyebrow block mb-1.5">Subject</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} className="span-input" required data-testid="bulk-subject" placeholder="Subject (supports {{first_name}}, {{company}}, {{month}}, {{year}})" />
              </div>
            )}

            <div>
              <label className="label-eyebrow block mb-1.5">Message body</label>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} className="span-input min-h-[200px] font-mono text-[12px]" required data-testid="bulk-body" placeholder="Hi {{first_name}}, ..." />
              <div className="text-[10px] text-[var(--span-text-2)] mt-1 font-mono uppercase tracking-[0.12em]">Variables: {"{{first_name}} {{last_name}} {{full_name}} {{company}} {{month}} {{year}} {{sender_name}}"}</div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer text-[12px]">
              <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} data-testid="bulk-test-mode" />
              Test mode — only send to first 2 recipients
            </label>

            <div className="card-flat p-3 text-[12px]">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={scheduleEnabled} onChange={(e) => setScheduleEnabled(e.target.checked)} data-testid="bulk-schedule-toggle" />
                <Calendar size={13} />
                Schedule for later instead of sending now
              </label>
              {scheduleEnabled && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    data-testid="bulk-scheduled-at"
                    className="span-input h-9 text-[12px]"
                  />
                  <span className="text-[10px] text-[var(--span-text-2)] font-mono uppercase tracking-[0.12em]">
                    queued at {new Date(scheduledAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {err && <div className="text-[var(--span-danger)] text-[12px]">{err}</div>}
            <div className="flex justify-end gap-2 pt-3 border-t border-[var(--span-border)]">
              <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
              <button data-testid="bulk-submit" disabled={busy} className="span-btn disabled:opacity-50">{busy ? "Queueing…" : scheduleEnabled ? `Schedule for ${recipients.length}` : `Send to ${recipients.length}`}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


function HolidayModal({ onClose }) {
  const [calendar, setCalendar] = useState([]);
  const [occasion, setOccasion] = useState("diwali");
  const [channel, setChannel] = useState("email");
  const [industry, setIndustry] = useState("");
  const [industries, setIndustries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/stakeholders/holiday-calendar").then(r => setCalendar(r.data.calendar || [])).catch(() => {});
    api.get("/stakeholders/industries").then(r => setIndustries(r.data.industries || [])).catch(() => {});
  }, []);

  const current = calendar.find(c => c.occasion === occasion);

  const schedule = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/stakeholders/schedule-holiday", {
        occasion, channel,
        industry_filter: industry || undefined,
      });
      setResult(data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Schedule failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card-flat w-full max-w-[560px] max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3.5 border-b border-[var(--span-border)] flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Set-and-forget</div>
            <div className="font-display font-bold text-[16px]">Holiday auto-scheduler</div>
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>

        {result ? (
          <div className="px-5 py-5 space-y-3 text-[13px]">
            <div className="text-[14px] font-semibold text-[var(--span-success,#10B981)]">Scheduled ✓</div>
            <div>SPAN will personalise and send to <strong>{result.recipients_total}</strong> stakeholders ({result.queued_email} email + {result.queued_whatsapp} WhatsApp) at <strong className="font-mono">{result.send_at}</strong>.</div>
            <div className="text-[11px] text-[var(--span-text-2)]">{result.note}</div>
            <div className="flex justify-end pt-2">
              <button onClick={onClose} className="span-btn">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={schedule} className="px-5 py-5 space-y-4 text-[12px]">
            <p className="text-[var(--span-text-2)] leading-relaxed">
              Pick a holiday — SPAN will personalise the greeting per stakeholder and queue it to ship at <strong>09:00 IST on the holiday date</strong>. No further action needed.
            </p>

            <div>
              <label className="label-eyebrow block mb-1.5">Occasion</label>
              <select value={occasion} onChange={(e) => setOccasion(e.target.value)} data-testid="holiday-occasion" className="span-input">
                {calendar.map(c => (
                  <option key={c.occasion} value={c.occasion}>{c.label}{c.next_send_date ? ` — ${c.next_send_date}` : " — no upcoming"}</option>
                ))}
              </select>
              {current?.next_send_date && (
                <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.12em] text-[var(--span-text-2)]">
                  Will send on {current.next_send_date} · 09:00 IST · template {"\u201C"}{current.template_key}{"\u201D"}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-1.5">Channel</label>
                <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="holiday-channel" className="span-input">
                  <option value="email">Email only</option>
                  <option value="whatsapp">WhatsApp only</option>
                  <option value="both">Both</option>
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Industry filter (optional)</label>
                <select value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="holiday-industry" className="span-input">
                  <option value="">All industries</option>
                  {industries.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            </div>

            {err && <div className="text-[var(--span-danger)]">{err}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-[var(--span-border)]">
              <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
              <button data-testid="holiday-submit" disabled={busy} className="span-btn disabled:opacity-50">{busy ? "Scheduling…" : "Schedule for everyone"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
